import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import './_tiptap.scss';
import './_tiptap.dark-mode.scss';
import { isArray, isString } from 'lodash';
import PropTypes from 'prop-types';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';

import Toolbar from './Toolbar';

import { convertStringToHtml } from '../../pages/Tickets/helpers';

const TipTap = forwardRef((props, ref) => {
  const { content, onChange, toolbar, extensions, position, onPaste, onDrop } = props;
  const editorExtensions = useMemo(() => {
    let currentExtensions = [];
    const startKitOptions = {
      bold: false,
      strike: false,
      italic: false,
      code: false,
      codeBlock: false,
      heading: false,
      blockquote: false,
      horizontalRule: false,
      bulletList: false,
      orderedList: false,
    };
    const checkIsAdded = (name) => !!currentExtensions.find((item) => item.name === name);
    if (toolbar) {
      toolbar.forEach((group) => {
        group.forEach((name) => {
          if (isString(name)) {
            const isAdded = checkIsAdded(name);
            if (!isAdded) {
              switch (name) {
              case 'bold':
                startKitOptions.bold = true;
                break;
              case 'italic':
                startKitOptions.italic = true;
                break;
              case 'hr':
                startKitOptions.horizontalRule = true;
                break;
              case 'blockquote':
                startKitOptions.blockquote = true;
                break;
              case 'ul':
                startKitOptions.bulletList = {
                  keepMarks: true,
                  keepAttributes: false,
                };
                break;
              case 'ol':
                startKitOptions.orderedList = {
                  keepMarks: true,
                  keepAttributes: false,
                };
                break;
              default:
              }
            }
          }
        });
      });
    }

    if (isArray(extensions) && extensions.length) {
      currentExtensions = [...currentExtensions, ...extensions];
    }

    return [StarterKit.configure(startKitOptions), ...currentExtensions];
  }, [extensions, toolbar]);

  const editor = useEditor({
    parseOptions: {
      preserveWhitespace: true,
    },
    editorProps: {
      transformPastedHTML(html) {
        return convertStringToHtml(html);
      },
      handlePaste(_, event) {
        const items = Array.from(event.clipboardData?.items || []);
        const files = [];
        items?.forEach((item) => {
          const file = item.getAsFile();
          if (file) files.push(file);
        });
        onPaste?.(files);
      },
      handleDrop(_, event) {
        const files = event?.dataTransfer?.files;
        if (files?.length) {
          event.preventDefault();
          onDrop?.(files);
        }
      },
    },
    extensions: [...editorExtensions],
    content,
    onUpdate: ({ editor }) => {
      if (onChange) {
        const html = editor.getHTML();
        if (html === '<p></p>') {
          onChange('');
          return;
        }
        onChange(html.replace(/<p><\/p>/g, '<p><br></p>'));
      }
    },
  });

  useImperativeHandle(ref, () => ({
    pasteHtml: (html) => {
      editor.commands.insertContent(html, {
        parseOptions: {
          preserveWhitespace: true,
        },
      });
      editor?.commands.focus();
    },
  }));

  if (!editor) {
    return null;
  }

  return (
    <div className="tiptap-editor__container">
      <Toolbar toolbar={toolbar} position={position} editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
});

TipTap.defaultProps = {
  position: 'top',
  content: '',
  extensions: [],
};

TipTap.propTypes = {
  content: PropTypes.string,
  onChange: PropTypes.func,
  toolbar: PropTypes.array,
  extensions: PropTypes.array,
  position: PropTypes.string,
  onPaste: PropTypes.func,
  onDrop: PropTypes.func,
};

export default TipTap;
